/**
 * Canonical phone-number display formatting (issue #401).
 *
 * This module is the single source of truth for turning a stored phone string
 * into the display format Mission PLC staff asked for: `555-123-4567` (plus
 * ` x123` for an extension). It is DISPLAY ONLY — it never changes what gets
 * stored, and it never throws.
 *
 * Scope: North American Numbering Plan (NANP) 10-digit numbers are dashed.
 * Anything else — international (`+44 …`, `+63 …`), partial, or junk — is
 * returned trimmed but otherwise untouched, so an international number or a
 * value we don't recognise is shown exactly as entered rather than mangled.
 *
 * Dialing/E.164 normalisation (twilioWebhooks / signalwireWebhooks) and the QBO
 * payload formatter are deliberately NOT built on this — they have their own
 * rules and are left alone.
 */

export interface NormalizedPhone {
  /** Digits of the national number with any country code and extension removed. */
  digits: string;
  /** Extension digits only (no `x`/`ext` marker), or '' when there is none. */
  ext: string;
  /** True only for a 10-digit NANP number with a valid area code (begins 2-9) — what we dash-format. */
  valid: boolean;
  /** The original input, trimmed. */
  raw: string;
}

// Extension suffix: x / ext / ext. / #  followed by digits, at the end of the string.
const EXT_SUFFIX = /(?:\s*(?:ext\.?|x|#)\s*)(\d+)\s*$/i;

/**
 * Break a raw phone string into its parts. Pure, total (never throws).
 *
 * - Strips everything but digits for the national number.
 * - An 11-digit number starting with `1` (or a `+1` prefix) is reduced to its
 *   10 national digits.
 * - A leading `+` that is NOT `+1` marks an international number: it is never a
 *   NANP number, whatever its digit count, so `valid` is false and it is left
 *   alone (e.g. `+44 …`, `+33 1 23 45 67 89`).
 * - A trailing extension (`x12`, `ext. 12`, `#12`) is pulled off into `ext`.
 * - `valid` is true only when 10 national digits remain AND the area code
 *   begins 2-9 (a dialable NANP number); `012…`/`123…` area codes stay raw.
 */
export function normalizePhone(raw: string | null | undefined): NormalizedPhone {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return { digits: '', ext: '', valid: false, raw: '' };

  // Pull a trailing extension off first so its digits don't pollute the count.
  let work = trimmed;
  let ext = '';
  const extMatch = work.match(EXT_SUFFIX);
  if (extMatch) {
    ext = extMatch[1];
    work = work.slice(0, extMatch.index).trim();
  }

  // A leading '+' that isn't the NANP country code (+1) is international — never
  // dashed, regardless of how many digits it has after stripping punctuation.
  const intlNonNanp = work.startsWith('+') && !/^\+\s*1/.test(work);

  let digits = work.replace(/\D/g, '');
  // US/Canada: a leading country code `1` (11 digits, or an explicit +1) → drop it.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  // A real NANP number is a 10-digit national number whose AREA CODE begins 2-9
  // (area codes starting 0 or 1 do not exist). This rejects inputs where a stray
  // leading/country-code digit slid into the area-code slot — e.g. `+1234567890`
  // would otherwise mis-format to `123-456-7890` (area code 123, invalid), and
  // `0123456789` to `012-345-6789`. Such values are now shown raw instead.
  //
  // NOTE: strict NANP also requires the EXCHANGE (central-office) code to begin
  // 2-9, but issue #401's canonical example `555-123-4567` has exchange `123`
  // (begins with 1). Enforcing an exchange 2-9 rule would make the issue's own
  // required display cases fail, so it is deliberately NOT applied. See the
  // "area code must begin 2-9" block in phone.test.ts.
  const valid = !intlNonNanp && digits.length === 10 && /[2-9]/.test(digits[0]);
  return { digits, ext, valid, raw: trimmed };
}

/**
 * Display a phone number as `555-123-4567` (plus ` x123`).
 *
 * - Empty / null / undefined → `''`.
 * - A valid 10-digit NANP number → dashed, with ` x<ext>` appended if present.
 * - Anything else (international, partial, junk) → the trimmed input unchanged,
 *   so nothing — including an extension on an unrecognised number — is lost.
 */
export function formatPhoneDisplay(raw: string | null | undefined): string {
  const { digits, ext, valid, raw: trimmed } = normalizePhone(raw);
  if (!trimmed) return '';
  if (valid) {
    const dashed = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    return ext ? `${dashed} x${ext}` : dashed;
  }
  return trimmed;
}
